import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from './config.service';
import {
  CentralOrganization,
  CentralPaginated,
  CentralSelfRequest,
  CentralTenant,
  CentralUser,
} from './shared/models/central.model';
import { map, Observable } from 'rxjs';
import { CachedResource, createCachedResource } from './shared/caching.utils';

interface GetTenantOptions {
  filterCurrentTenant?: boolean;
  filterInactiveTenants?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CentralService {

  orgId: string;
  managerUrl: string;
  tenantPath: string;

  private currentOrg: CachedResource<CentralOrganization>;

  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) {
    this.orgId = this.config.getCentralConfig().organization_id;
    this.managerUrl = this.config.getCentralConfig().manager_url;
    this.tenantPath = this.config.getCentralConfig().tenant_path;

    this.currentOrg = createCachedResource(this.http.get<CentralOrganization>(`${this.managerUrl}organizations/${this.orgId}`));
  }

  isCentralTenant(): boolean {
    return this.config.getCentralConfig()?.enabled;
  }

  getSelf(): Observable<CentralUser>{
    return this.http.get<CentralUser>(`${this.managerUrl}users/self`);
  }

  putSelf(body: CentralSelfRequest): Observable<CentralUser>{
    return this.http.put<CentralUser>(`${this.managerUrl}users/self`, body);
  }

  getCurrentOrg(): Observable<CentralOrganization> {
    return this.currentOrg.result$;
  }

  getTenants(options: GetTenantOptions = { filterCurrentTenant: true, filterInactiveTenants: true }): Observable<CentralTenant[]> {

    const { filterCurrentTenant, filterInactiveTenants } = options;

    return this.http.get<CentralPaginated<CentralTenant>>(`${this.managerUrl}organizations/${this.orgId}/tenants`).pipe(
      map(res => {

        return res.items.filter(tenant => {
          const isCurrentTenant = tenant.path === this.tenantPath;
          const isInactiveTenant = tenant.deployment_status !== 'deployed';
          const shouldBeFiltered = (filterCurrentTenant && isCurrentTenant) || (filterInactiveTenants && isInactiveTenant);

          return !shouldBeFiltered;
        })
        .sort((ten1, ten2) => (ten1.label < ten2.label ? -1 : 1)); // sorts alphabetically
      })
    );
  }

  getCurrentTenant(): Observable<CentralTenant> {
    return this.getTenants({ filterCurrentTenant: false }).pipe(
      map(tenants => {
        return tenants.find(ten => ten.path === this.tenantPath);
      })
    );
  }

  getUserManagementUrl(): Observable<string> {
    return this.getCurrentOrg().pipe(
      map(org => `${this.config.getCentralConfig().app_url}org/${this.getOrgSlug(org)}/users`)
    );
  }

  private getOrgSlug (org: CentralOrganization): string {
    return org.name.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // remove any non-alphanumeric chars
      .replace(/[\s_-]+/g, '-') // switch whitespace and _ to -
      .replace(/^-+|-+$/g, ''); // clean up - at the beginning and end
  }
}
